/**
 * Endpoints #77-#85 — comment generation (SPEC `Comment Generation`).
 *
 * Two halves with different cache shapes, both answering with a *whole* read model so the client
 * never has to reassemble one:
 *  - the Settings tab (#77-#81): every write answers with the full #77 payload, exactly as the
 *    Scrapers and Notion tabs do, so the section replaces its cache instead of refetching;
 *  - the chat (#82-#85): every mutation answers with the full #82 payload, so a send is one request
 *    and one `setQueryData` — no invalidation fan-out behind an open popup.
 *
 * #85 (`regenerate`) is deliberately one endpoint for two buttons: the error row's Retry (the tail
 * is a user message, nothing to supersede) and an assistant bubble's Regenerate.
 */

import type {
  AiPromptsUpdateBody,
  AiTabDTO,
  AiTestResponse,
  CommentChatDTO,
} from '@lw/shared';
import { apiJson } from './client';

/* --------------------------------------------------------------- #77-#81 tab */

/** #77 `GET /api/ai` — tab state + readiness; secrets arrive masked (§10.3). No provider call. */
export function getAi(signal?: AbortSignal): Promise<AiTabDTO> {
  return apiJson<AiTabDTO>('/ai', { signal });
}

/** #78 `PUT /api/ai/backends/:id/config` — partial; an omitted secret keeps its stored value. */
export function putAiBackendConfig(
  backendId: string,
  config: Record<string, string | number | boolean>,
): Promise<AiTabDTO> {
  return apiJson<AiTabDTO>(`/ai/backends/${encodeURIComponent(backendId)}/config`, {
    method: 'PUT',
    body: { config },
  });
}

/** #79 `POST /api/ai/backends/:id/test` — `{ok}` either way; never throws on a bad key. */
export function testAiBackend(backendId: string): Promise<AiTestResponse> {
  return apiJson<AiTestResponse>(`/ai/backends/${encodeURIComponent(backendId)}/test`, {
    method: 'POST',
  });
}

/** #80 `PUT /api/ai/active` — bind the generation slot, or `null` for None (disabled). */
export function putAiActive(backendId: string | null): Promise<AiTabDTO> {
  return apiJson<AiTabDTO>('/ai/active', { method: 'PUT', body: { backendId } });
}

/** #81 `PUT /api/ai/prompts` — the whole pair; the tab holds one draft and one Save. */
export function putAiPrompts(body: AiPromptsUpdateBody): Promise<AiTabDTO> {
  return apiJson<AiTabDTO>('/ai/prompts', { method: 'PUT', body });
}

/* -------------------------------------------------------------- #82-#85 chat */

/** #82 `GET /api/posts/:id/comment-chat` — resume: the active conversation, or `conversation: null`. */
export function getCommentChat(postId: number, signal?: AbortSignal): Promise<CommentChatDTO> {
  return apiJson<CommentChatDTO>(`/posts/${postId}/comment-chat`, { signal });
}

/**
 * #83 `POST /api/posts/:id/comment-chat` — archive any active chat, start a fresh one on `content`,
 * generate once.
 *
 * The body is #84's: the first message of a chat is a message, whether the user pressed a preset or
 * typed it. "New chat" needs no separate call — the server archives whatever was active inside the
 * same transaction, so a hidden chat is only really replaced once something is actually sent.
 */
export function startCommentChat(postId: number, content: string): Promise<CommentChatDTO> {
  return apiJson<CommentChatDTO>(`/posts/${postId}/comment-chat`, {
    method: 'POST',
    body: { content },
  });
}

/** #84 `POST /api/ai/conversations/:id/messages` — append one user turn and generate one reply. */
export function sendCommentChatMessage(
  conversationId: number,
  content: string,
): Promise<CommentChatDTO> {
  return apiJson<CommentChatDTO>(`/ai/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: { content },
  });
}

/** #85 `POST /api/ai/conversations/:id/regenerate` — Retry *and* Regenerate; adds no message. */
export function regenerateCommentChat(conversationId: number): Promise<CommentChatDTO> {
  return apiJson<CommentChatDTO>(`/ai/conversations/${conversationId}/regenerate`, {
    method: 'POST',
  });
}
